import { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { useTranslation } from "react-i18next";
import {
  cancelVideoByToken,
  CancelVideoError,
  CancelVideoErrorKind
} from "../../services/appointments";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

type Status = "confirm" | "cancelling" | "success" | "error" | "dismissed";

const pageSx = {
  minHeight: "100vh",
  bgcolor: semanticColors.surface.containerLow,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: { xs: "16px", sm: "24px" }
};

const cardSx = {
  width: "100%",
  maxWidth: "460px",
  bgcolor: semanticColors.surface.canvas,
  borderRadius: "24px",
  boxShadow: `0 8px 32px ${semanticColors.surface.inverse}1F`,
  p: { xs: "32px 24px", sm: "40px" },
  textAlign: "center" as const,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px"
};

const iconSx = { fontSize: 56 };

const successIconSx = { ...iconSx, color: semanticColors.feedback.ok };

const errorIconSx = { ...iconSx, color: semanticColors.feedback.danger };

const confirmIconSx = { ...iconSx, color: semanticColors.action.primary };

const spinnerSx = { color: semanticColors.text.primary };

const titleSx = {
  ...typography.heading.m,
  color: semanticColors.text.primary
};

const messageSx = {
  ...typography.body.l,
  color: semanticColors.text.secondary
};

const hintSx = {
  ...typography.body.m,
  color: semanticColors.text.subtle
};

const buttonSx = (disabled: boolean) => ({
  mt: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  width: "100%",
  height: "48px",
  borderRadius: "40px",
  border: `1px solid ${semanticColors.feedback.danger}`,
  background: semanticColors.surface.container,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1
});

const buttonLabelSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

// "Voltar" — the reassuring action (keeps the appointment), so it carries the
// app's primary green gradient. Its label sits on the gradient, hence inverse.
const dismissButtonSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "48px",
  borderRadius: "40px",
  border: "none",
  background: semanticColors.gradient.surface,
  cursor: "pointer"
};

const dismissLabelSx = {
  ...typography.body.lBold,
  color: semanticColors.text.inverse
};

// Each failure reason maps to its own message. Titles stay generic for
// `cannotCancel`/`notFound`; the server error gets its own title.
const errorMessageKey = {
  cannotCancel: "cancelVideo.errorCannotCancel",
  notFound: "cancelVideo.errorNotFound",
  server: "cancelVideo.errorServer",
  generic: "cancelVideo.errorMessage"
} as const;

export function CancelVideo() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>("confirm");
  const [errorKind, setErrorKind] = useState<CancelVideoErrorKind>("generic");

  const handleCancel = async () => {
    setStatus("cancelling");
    try {
      await cancelVideoByToken(token);
      setStatus("success");
    } catch (err) {
      setErrorKind(err instanceof CancelVideoError ? err.kind : "generic");
      setStatus("error");
    }
  };

  // Best-effort close: window.close() only works on script-opened windows, so
  // for a tab opened from the email link the browser silently blocks it. We
  // optimistically switch to the "dismissed" state — if the tab does close the
  // user never sees it; if it doesn't, they get the "close this window" hint.
  const handleDismiss = () => {
    window.close();
    setStatus("dismissed");
  };

  return (
    <Box sx={pageSx}>
      <Box sx={cardSx}>
        {status === "success" ? (
          <>
            <CheckCircleOutlineIcon sx={successIconSx} />
            <Typography sx={titleSx}>
              {t("cancelVideo.successTitle")}
            </Typography>
            <Typography sx={messageSx}>
              {t("cancelVideo.successMessage")}
            </Typography>
            <Typography sx={hintSx}>{t("cancelVideo.closeHint")}</Typography>
          </>
        ) : status === "error" ? (
          <>
            <ErrorOutlineIcon sx={errorIconSx} />
            <Typography sx={titleSx}>
              {t(
                errorKind === "server"
                  ? "cancelVideo.errorServerTitle"
                  : "cancelVideo.errorTitle"
              )}
            </Typography>
            <Typography sx={messageSx}>
              {t(errorMessageKey[errorKind])}
            </Typography>
          </>
        ) : status === "dismissed" ? (
          <>
            <CheckCircleOutlineIcon sx={successIconSx} />
            <Typography sx={titleSx}>
              {t("cancelVideo.dismissTitle")}
            </Typography>
            <Typography sx={hintSx}>{t("cancelVideo.closeHint")}</Typography>
          </>
        ) : (
          <>
            <EventBusyOutlinedIcon sx={confirmIconSx} />
            <Typography sx={titleSx}>{t("cancelVideo.title")}</Typography>
            <Typography sx={messageSx}>
              {t("cancelVideo.confirmMessage")}
            </Typography>
            <Box
              component="button"
              onClick={handleCancel}
              disabled={status === "cancelling"}
              sx={buttonSx(status === "cancelling")}
            >
              {status === "cancelling" ? (
                <>
                  <CircularProgress size={18} sx={spinnerSx} />
                  <Typography sx={buttonLabelSx}>
                    {t("cancelVideo.cancelling")}
                  </Typography>
                </>
              ) : (
                <Typography sx={buttonLabelSx}>
                  {t("cancelVideo.confirmButton")}
                </Typography>
              )}
            </Box>
            <Box
              component="button"
              onClick={handleDismiss}
              disabled={status === "cancelling"}
              sx={dismissButtonSx}
            >
              <Typography sx={dismissLabelSx}>
                {t("cancelVideo.dismissButton")}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
